import { Box, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getMarginSummary } from 'api/managementReportApi';
import type { MarginSummaryCategory } from 'api/managementReportApi';
import PageLoader from 'components/Spinners/PageLoader';
import React, { useEffect, useMemo, useState } from 'react';
import MarginSummaryCategoryChips from './MarginSummaryCategoryChips';
import MarginSummaryCategoryPanel from './MarginSummaryCategoryPanel';
import { sortAverageMarginsByCategory } from './marginSummaryLabels';

const MarginSummaryHomePage = () => {
  const { data, isLoading, error } = useQuery(
    ['margin-summary'],
    getMarginSummary
  );
  const sortedSummaries = useMemo(
    () => (data?.length ? sortAverageMarginsByCategory(data) : []),
    [data]
  );
  const [selectedCategory, setSelectedCategory] = useState<
    MarginSummaryCategory | ''
  >('');
  const [pageByCategory, setPageByCategory] = useState<Record<string, number>>(
    {}
  );
  const [rowsPerPageByCategory, setRowsPerPageByCategory] = useState<
    Record<string, number>
  >({});

  const categorySummary = useMemo(() => {
    if (sortedSummaries.length === 0) return undefined;
    return (
      sortedSummaries.find((s) => s.category === selectedCategory) ??
      sortedSummaries[0]
    );
  }, [sortedSummaries, selectedCategory]);

  useEffect(() => {
    if (sortedSummaries.length === 0) return;
    const hasCurrentSelection = sortedSummaries.some(
      (summary) => summary.category === selectedCategory
    );
    if (!selectedCategory || !hasCurrentSelection) {
      setSelectedCategory(sortedSummaries[0].category);
    }
  }, [sortedSummaries, selectedCategory]);

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh">
        <Typography color="error">
          Error loading margin summary data.
        </Typography>
      </Box>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="50vh">
        <Typography>No margin summary data available.</Typography>
      </Box>
    );
  }

  const handleChangePage = (category: string, newPage: number) => {
    setPageByCategory((prev) => ({
      ...prev,
      [category]: newPage,
    }));
  };

  const handleChangeRowsPerPage = (
    category: string,
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const value = Number.parseInt(event.target.value, 10);
    setRowsPerPageByCategory((prev) => ({
      ...prev,
      [category]: value,
    }));
    setPageByCategory((prev) => ({
      ...prev,
      [category]: 0,
    }));
  };

  if (!categorySummary) {
    return null;
  }

  const page = pageByCategory[categorySummary.category] || 0;
  const rowsPerPage =
    rowsPerPageByCategory[categorySummary.category] || 10;

  return (
    <Box sx={{ m: 3 }}>
      <MarginSummaryCategoryChips
        summaries={sortedSummaries}
        selectedCategory={selectedCategory}
        onSelect={setSelectedCategory}
      />
      <MarginSummaryCategoryPanel
        categorySummary={categorySummary}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={(newPage) =>
          handleChangePage(categorySummary.category, newPage)
        }
        onRowsPerPageChange={(event) =>
          handleChangeRowsPerPage(categorySummary.category, event)
        }
      />
    </Box>
  );
};

export default MarginSummaryHomePage;
